class SearchInput extends HTMLElement {
  constructor () {
    super()

    this.props = {
      translations: window.theme.tagalys.searchAutocomplete.translations,
      voiceOverDelay: 800,
    }

    this.elements = {
      input: this.querySelector('[data-search-input-el="input"]'),
      form: this.querySelector('form'),
      close: this.querySelector('[data-search-input-el="close"]'),
      backdrop: document.querySelector('[data-search-dropdown-el="backdrop"]'),
      submit: this.querySelector('[data-search-input-el="submit"]'),
      ariaLiveHappy: this.querySelector('[data-search-input-el="aria-live-happy"]'),
      ariaLiveSad: this.querySelector('[data-search-input-el="aria-live-sad"]'),
    }

    this.externalElements = {
      dropdown: document.querySelector('[data-search-input-external-el="dropdown"]'),
      container: document.querySelector('[data-search-input-external-el="container"]'),
    }

    // Add timeout ID for debouncing accessibility announcements
    this.a11yAnnouncementTimeout = null
    // Track last announced state to prevent duplicate announcements
    this.lastAnnouncedState = {
      term: null,
      totalResults: null,
    }

    // Track pending form submission (for race condition with API)
    this.pendingSubmit = false
    // Store the last API response to check if we have results for current term
    this.lastApiResponse = null
    // Track the term that was searched when API returned
    this.lastApiTerm = null

    // Store bound handlers for cleanup
    this._boundHandlers = {
      handleInput: this.handleInput.bind(this),
      handleDropdownOpen: this.handleDropdownOpen.bind(this),
      handleDropdownClose: this.handleDropdownClose.bind(this),
      handleFormSubmit: this.handleFormSubmit.bind(this),
      handleA11yTermUpdated: this.handleA11yTermUpdated.bind(this),
      handleApiResultsFetched: this.handleApiResultsFetched.bind(this),
    }

    this.open = false
  }

  connectedCallback () {
    this.attachEventListeners()
  }

  disconnectedCallback () {
    this.detachEventListeners()
  }

  attachEventListeners () {
    if (this.elements.input) {
      this.elements.input.addEventListener('input', this._boundHandlers.handleInput)
      this.elements.input.addEventListener('focus', this._boundHandlers.handleDropdownOpen)
    }

    if (this.elements.close) {
      this.elements.close.addEventListener('click', this._boundHandlers.handleDropdownClose)
    }
    if (this.elements.backdrop) {
      this.elements.backdrop.addEventListener('click', this._boundHandlers.handleDropdownClose)
    }

    window.addEventListener('mobile-nav-drawer:ui:open', this._boundHandlers.handleDropdownClose)

    this.addEventListener('submit', this._boundHandlers.handleFormSubmit)

    window.theme.tagalys.searchAutocomplete.eventBus.subscribe('searchAutoComplete:state:updated', this._boundHandlers.handleA11yTermUpdated)
    window.theme.tagalys.searchAutocomplete.eventBus.subscribe('searchAutoComplete:api:results:fetched', this._boundHandlers.handleApiResultsFetched)
  }

  detachEventListeners () {
    if (this.elements.input) {
      this.elements.input.removeEventListener('input', this._boundHandlers.handleInput)
      this.elements.input.removeEventListener('focus', this._boundHandlers.handleDropdownOpen)
    }

    if (this.elements.close) {
      this.elements.close.removeEventListener('click', this._boundHandlers.handleDropdownClose)
    }

    window.removeEventListener('mobile-nav-drawer:ui:open', this._boundHandlers.handleDropdownClose)

    this.removeEventListener('submit', this._boundHandlers.handleFormSubmit)

    window.theme.tagalys.searchAutocomplete.eventBus.unsubscribe('searchAutoComplete:state:updated', this._boundHandlers.handleA11yTermUpdated)
    window.theme.tagalys.searchAutocomplete.eventBus.unsubscribe('searchAutoComplete:api:results:fetched', this._boundHandlers.handleApiResultsFetched)

    // Clear any pending timeout
    if (this.a11yAnnouncementTimeout) {
      clearTimeout(this.a11yAnnouncementTimeout)
    }
  }

  handleInput () {
    const searchTerm = this.elements.input.value.trim()

    // Reset API response tracking when term changes
    this.lastApiResponse = null
    this.lastApiTerm = null

    window.theme.tagalys.searchAutocomplete.eventBus.publish('searchAutoComplete:ui:term:updated', {
      term: searchTerm,
    })
  }

  handleApiResultsFetched (response) {
    const currentTerm = this.elements.input.value.trim()
    this.lastApiResponse = response
    this.lastApiTerm = currentTerm

    // If user submitted before API returned, perform the redirect now
    if (this.pendingSubmit) {
      this.pendingSubmit = false
      this.performRedirect(response, this.lastApiTerm)
    }
  }

  performRedirect (response, searchTerm) {
    const marketUrl = window.location.origin + window.Shopify.routes.root

    let redirectUrl
    if (response?.redirect_url) {
      redirectUrl = response.redirect_url
    } else {
      redirectUrl = `${marketUrl}search?q=${encodeURIComponent(searchTerm)}`
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'search',
        search_term: searchTerm,
      })
    }

    window.location.href = redirectUrl
  }

  handleA11yTermUpdated (results) {
    if (!this.elements.ariaLiveHappy || !this.elements.ariaLiveSad) return

    // Clear any existing timeout to prevent multiple announcements
    if (this.a11yAnnouncementTimeout) {
      clearTimeout(this.a11yAnnouncementTimeout)
    }

    // Check if we've already announced this exact state to prevent duplicates
    const currentState = {
      term: results.term,
      totalResults: results.products.length,
    }

    if (this.lastAnnouncedState.term === currentState.term &&
        this.lastAnnouncedState.totalResults === currentState.totalResults) {
      return // Skip announcement if it's the same as the last one
    }

    // Delay the announcement to allow VoiceOver to finish reading current content
    this.a11yAnnouncementTimeout = setTimeout(() => {
      if (results.products.length > 0) {
        this.elements.ariaLiveHappy.textContent = `${results.products.length} ${this.props.translations.ariaLiveHappy} '${results.term}'`
        this.elements.ariaLiveSad.textContent = ''
      } else {
        this.elements.ariaLiveSad.textContent = `${this.props.translations.ariaLiveSad} '${results.term}'`
        this.elements.ariaLiveHappy.textContent = ''
      }

      // Update the last announced state
      this.lastAnnouncedState = currentState
    }, this.props.voiceOverDelay)
  }

  handleFormSubmit (event) {
    event.preventDefault()
    const searchTerm = this.elements.input.value.trim()

    if (!searchTerm) return

    // Check if API has already returned for the current search term
    if (this.lastApiResponse && this.lastApiTerm === searchTerm) {
      // API has returned, redirect immediately
      this.performRedirect(this.lastApiResponse, searchTerm)
    } else {
      // API hasn't returned yet, queue the submission
      this.pendingSubmit = true
    }
  }

  handleDropdownOpen () {
    if (this.open === true) return

    window.theme.tagalys.searchAutocomplete.eventBus.publish('searchAutoComplete:ui:dropdown:open')

    this.elements.close?.classList.remove('hidden')
    this.elements.submit?.classList.add('hidden')
    this.elements.input.focus()

    document.body.style.overflow = 'hidden'

    const customEvent = new CustomEvent('mobile-nav-drawer:ui:close')
    window.dispatchEvent(customEvent)
    this.open = true
  }

  handleDropdownClose () {
    if (this.open === false) return

    window.theme.tagalys.searchAutocomplete.eventBus.publish('searchAutoComplete:ui:dropdown:close')

    this.elements.close?.classList.add('hidden')
    this.elements.submit?.classList.remove('hidden')
    this.elements.input.value = ''
    this.lastApiResponse = null
    this.lastApiTerm = null
    this.elements.input.blur()

    window.theme.tagalys.searchAutocomplete.eventBus.publish('searchAutoComplete:ui:term:updated', { term: '' })

    document.body.style.overflow = 'visible'
    this.open = false
  }
}

customElements.define('search-input', SearchInput)
